import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldHalved, faCheck, faXmark, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import { psGet, psPost } from "./PSApiClient";
import { ui, PrimaryButton, GhostButton, EmptyState, ErrorState } from "./psShared";

export default function PSAdminApprovals() {
  const [tab, setTab] = useState("chapters"); // 'chapters' | 'circles'
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    setError(null);
    setSelected(null);
    psGet(tab === "chapters" ? "/admin/chapters/pending" : "/admin/circles/pending")
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tab]);

  const decide = async (approve) => {
    if (!selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const path =
        tab === "chapters"
          ? `/admin/chapters/${selected._id}/decision`
          : `/admin/circles/${selected._id}/decision`;
      await psPost(path, { activate: approve, remarks });
      setRemarks("");
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <h2 className="flex items-center gap-2 text-base lg:text-xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
        <FontAwesomeIcon icon={faShieldHalved} className="text-fuchsia-300" />
        Pending Approvals
      </h2>
      <p className="mt-1 text-[12px] lg:text-sm text-white/45">
        Review new chapters and circles before they go live.
      </p>

      <div className="mt-4 flex gap-2">
        {[
          { id: "chapters", label: "Chapters" },
          { id: "circles", label: "Circles" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={[
              "rounded-full w-fit px-3 py-1.5 text-[11px] lg:text-sm font-semibold transition",
              tab === t.id ? ui.gradientBtn : "border border-white/10 bg-white/[0.03] text-white/60 hover:text-white",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="mt-4"><ErrorState message={error} onRetry={load} /></div>}

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <div className="grid gap-2">
          {loading &&
            Array.from({ length: 3 }).map((_, i) => <div key={i} className={`${ui.card} h-20 animate-pulse`} />)}

          {!loading && items.length === 0 && (
            <EmptyState icon={faShieldHalved} title="Nothing pending" hint="You're all caught up." />
          )}

          {!loading &&
            items.map((item) => (
              <button
                key={item._id}
                onClick={() => { setSelected(item); setRemarks(""); }}
                className={[
                  "rounded-2xl border p-4 text-left shadow-sm transition",
                  selected?._id === item._id
                    ? "border-fuchsia-400/50 bg-fuchsia-400/[0.08]"
                    : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
                ].join(" ")}
              >
                <p className="font-bold text-white text-[13px] lg:text-base">{item.name}</p>
                <p className="flex items-center gap-1 text-[11px] lg:text-sm text-white/45">
                  <FontAwesomeIcon icon={faLocationDot} className="text-[10px]" />
                  {item.location}
                </p>
                <p className="mt-1 text-[10px] lg:text-xs text-white/35">
                  Director: {item.directorUserId?.name || "—"}
                </p>
              </button>
            ))}
        </div>

        {selected && (
          <div className={`${ui.card} h-fit p-4`}>
            <h3 className="text-[13px] lg:text-base font-bold text-white">{selected.name}</h3>
            <p className="text-[11px] lg:text-sm text-white/45">{selected.location}</p>
            {selected.description && <p className="mt-2 text-[12px] lg:text-sm text-white/60">{selected.description}</p>}
            <p className="mt-2 text-[10px] lg:text-xs text-white/35">
              Director: {selected.directorUserId?.name} · {selected.directorUserId?.city || ""}
            </p>

            <label className={`${ui.label} mt-4`}>Remarks (shown to the director if declined)</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              placeholder="Optional note explaining the decision…"
              className={`${ui.input} mt-1`}
            />

            <div className="mt-4 flex gap-3">
              <GhostButton className="flex-1 justify-center" onClick={() => decide(false)} disabled={submitting}>
                <FontAwesomeIcon icon={faXmark} className="mr-1" />
                Decline
              </GhostButton>
              <PrimaryButton className="flex-1 justify-center" onClick={() => decide(true)} disabled={submitting}>
                <FontAwesomeIcon icon={faCheck} className="mr-1" />
                {submitting ? "Saving…" : "Activate"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
